import { useCallback, useEffect, useRef, useState, type RefObject } from "react";
import { chatHub } from "@/services/websocket/chat-hub";
import { SOCKET_CHAT_EVENTS } from "@/services/websocket/socket.enum";
import type { IChatConversationDetail, IChatMessage } from "@/types/(my-space)/chat.interface";
import {
  EMPTY_MENTION_QUEUE,
  MENTION_SEEN_RATIO,
  addMention,
  consumeOldestMention,
  isMentionSeen,
  removeMentions,
  seedMentionQueue,
  tagsMe,
  type MentionQueue,
} from "../_lib/mention-queue";

interface Options {
  conversationId: number;
  myUserId: number | null | undefined;
  detail: IChatConversationDetail | undefined;
  /** This visit's first detail has resolved — the only one that seeds. */
  detailReady: boolean;
  /** Following the live end (attached, at the bottom, tab visible): a new tag is seen as it lands. */
  isFollowingLive: () => boolean;
  /** The scrolling message list; its rows carry `data-message-id`. */
  scrollRef: RefObject<HTMLElement | null>;
  /** Re-observes when the rendered rows change. */
  messages: readonly IChatMessage[];
  /** Off while the rows are covered (search results): covered isn't seen. */
  observe: boolean;
}

/**
 * The room's "jump to my mentions" queue (see `_lib/mention-queue`): seeded
 * once from this visit's first detail, fed by live tags I'd miss, emptied by
 * jumping and by the tagged bubbles scrolling into view.
 */
export const useMentionQueue = ({
  conversationId,
  myUserId,
  detail,
  detailReady,
  isFollowingLive,
  scrollRef,
  messages,
  observe,
}: Options) => {
  // Tagged with its room: a queue never carries over to another conversation.
  const [state, setState] = useState({ conversationId, queue: EMPTY_MENTION_QUEUE });
  const queue = state.conversationId === conversationId ? state.queue : EMPTY_MENTION_QUEUE;

  const update = useCallback(
    (fn: (q: MentionQueue) => MentionQueue) =>
      setState((s) => {
        const current = s.conversationId === conversationId ? s.queue : EMPTY_MENTION_QUEUE;
        const next = fn(current);
        return next === current && s.conversationId === conversationId
          ? s
          : { conversationId, queue: next };
      }),
    [conversationId],
  );

  // Seed once; a later refetch (after reading, it returns []) never re-seeds.
  useEffect(() => {
    if (detailReady && detail?.id === conversationId) {
      update((q) => seedMentionQueue(q, detail.unreadMentionIds ?? []));
    }
  }, [conversationId, detail, detailReady, update]);

  const followingRef = useRef(isFollowingLive);
  followingRef.current = isFollowingLive;

  useEffect(
    () =>
      chatHub.subscribe((event, payload) => {
        const m = payload as IChatMessage | undefined;
        if (m?.conversationId !== conversationId) return;
        if (event === SOCKET_CHAT_EVENTS.MESSAGE_NEW) {
          if (tagsMe(m, myUserId) && !followingRef.current()) update((q) => addMention(q, m.id));
        } else if (event === SOCKET_CHAT_EVENTS.MESSAGE_UPDATED && m.isDeleted) {
          // Nothing left to jump to.
          update((q) => removeMentions(q, [m.id]));
        }
      }),
    [conversationId, myUserId, update],
  );

  // A queued bubble that is (mostly) on screen has been read.
  const ids = queue.ids;
  useEffect(() => {
    const root = scrollRef.current;
    if (!observe || !root || !ids.length || typeof IntersectionObserver === "undefined") return;
    const io = new IntersectionObserver(
      (entries) => {
        const seen = entries
          .filter((e) =>
            isMentionSeen({
              isIntersecting: e.isIntersecting,
              intersectionRatio: e.intersectionRatio,
              visibleHeight: e.intersectionRect.height,
              rootHeight: e.rootBounds?.height ?? null,
            }),
          )
          .map((e) => Number((e.target as HTMLElement).dataset.messageId));
        if (seen.length) update((q) => removeMentions(q, seen));
      },
      // Several steps so a bubble taller than the viewport still reports.
      { root, threshold: [0.2, 0.4, MENTION_SEEN_RATIO, 0.8, 1] },
    );
    ids.forEach((id) => {
      const row = root.querySelector(`[data-message-id="${id}"]`);
      if (row) io.observe(row);
    });
    return () => io.disconnect();
  }, [ids, messages, observe, scrollRef, update]);

  /** Removes and returns the oldest queued mention (null when none). */
  const takeOldest = useCallback(() => {
    const { id } = consumeOldestMention(queue);
    if (id != null) update((q) => removeMentions(q, [id]));
    return id;
  }, [queue, update]);

  return { count: ids.length, takeOldest };
};
